// src/feature-module/mentor/shadowing/MentorBatchShadowingTaskSummary.tsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaHome, FaSpinner, FaClipboardList } from "react-icons/fa";
import PageHeader from "../../../core/common/header/PageHeader";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

type Batch = {
  id: number;
  name: string;
  topic: string;
};

const MentorBatchShadowingTaskSummary: React.FC = () => {
  const [batches, setBatches] = useState<Batch[]>([]);
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchBatches();
  }, []);

  const fetchBatches = async () => {
    setLoadingBatches(true);
    setError("");
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/mentor/shadowing-tasks/my-batches`,
        { credentials: "include" }
      );

      if (response.ok) {
        const data = await response.json();
        setBatches(data);
        if (data.length > 0) {
          setSelectedBatchId(data[0].id);
        }
      } else {
        setError("Failed to load batches");
      }
    } catch (err) {
      console.error("Error fetching batches:", err);
      setError("Failed to load batches");
    } finally {
      setLoadingBatches(false);
    }
  };

  useEffect(() => {
    if (!selectedBatchId) {
      setResults([]);
      return;
    }
    fetchResults(selectedBatchId);
  }, [selectedBatchId]);

  const fetchResults = async (batchId: number) => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/mentor/shadowing-tasks/batches/${batchId}/results`,
        { credentials: "include" }
      );

      if (response.ok) {
        const data = await response.json();
        setResults(data);
      } else {
        setResults([]);
        setError("Failed to load batch results");
      }
    } catch (err) {
      console.error("Error fetching results:", err);
      setResults([]);
      setError("Failed to load batch results");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaClipboardList />}
        title="Batch Shadowing Task Summary"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/mentor/dashboard" },
          { label: "Shadowing Tasks", to: "/mentor/shadowing-tasks" },
          { label: "Batch Summary" },
        ]}
      />

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="bg-white rounded-xl shadow-lg p-6">
          {/* Batch Selector */}
          <div className="mb-6">
            <label className="font-bold text-gray-700 mr-3">
              Select Batch:
            </label>
            <select
              className="border border-gray-300 px-4 py-2 rounded focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
              style={{ fontSize: "16px" }}
              onChange={(e) => setSelectedBatchId(Number(e.target.value))}
              value={selectedBatchId ?? ""}
              disabled={loadingBatches}
            >
              {loadingBatches ? (
                <option>Loading...</option>
              ) : (
                <>
                  {batches.length === 0 && (
                    <option value="">No batches available</option>
                  )}
                  {batches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name} ({b.topic})
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 mb-4 rounded-lg">
              {error}
            </div>
          )}

          {/* Loading State */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <FaSpinner className="animate-spin text-[#009fe3] text-4xl" />
              <span className="ml-3 text-gray-600">Loading results...</span>
            </div>
          ) : selectedBatchId && results.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse border font-sans text-sm">
                <thead>
                  <tr className="bg-[#009fe3] text-white">
                    <th className="border px-4 py-2 text-left font-semibold">
                      Student
                    </th>
                    <th className="border px-4 py-2 text-center font-semibold">
                      Task #
                    </th>
                    <th className="border px-4 py-2 text-left font-semibold">
                      Description
                    </th>
                    <th className="border px-4 py-2 text-center font-semibold">
                      Submitted
                    </th>
                    <th className="border px-4 py-2 text-center font-semibold">
                      Submitted At
                    </th>
                    <th className="border px-4 py-2 text-center font-semibold">
                      Reviewed
                    </th>
                    <th className="border px-4 py-2 text-left font-semibold">
                      Feedback
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((result, index) => (
                    <tr key={index} className="hover:bg-blue-50">
                      <td className="border px-4 py-2 font-semibold text-gray-900">
                        {result.studentName}
                      </td>
                      <td className="border px-4 py-2 text-center">
                        <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-800 font-bold rounded-md text-sm">
                          {result.taskNumber}
                        </span>
                      </td>
                      <td className="border px-4 py-2 text-gray-700 text-sm">
                        {result.taskDescription || "No description"}
                      </td>
                      <td className="border px-4 py-2 text-center">
                        {result.submitted ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                            Yes
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                            No
                          </span>
                        )}
                      </td>
                      <td className="border px-4 py-2 text-center text-sm text-gray-600">
                        {result.submittedAt
                          ? new Date(result.submittedAt).toLocaleString(
                              "en-GB",
                              {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )
                          : "-"}
                      </td>
                      <td className="border px-4 py-2 text-center">
                        {result.reviewed ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                            Yes
                          </span>
                        ) : result.submitted ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                            Pending
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="border px-4 py-2 text-gray-700 text-sm">
                        {result.mentorFeedback || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            !loading && (
              <div className="text-center py-12 text-gray-500">
                {selectedBatchId
                  ? "No results available for this batch."
                  : "Please select a batch to view results."}
              </div>
            )
          )}
        </div>
      </main>
    </div>
  );
};

export default MentorBatchShadowingTaskSummary;
